/**
 * TournamentParticipantEditor
 *
 * Shared component for configuring participant assignment on a tournament.
 * Used in:
 *   - TournamentBuilder Step 3 (creation-time)
 *   - TournamentDetail RoundDetailPanel (post-creation edit)
 *
 * Three modes:
 *   fixed     — one shared member picker; stored as config.defaultParticipantIds
 *   per_round — stacked rows, one per round; each row controls round.participantIds
 *   open      — informational; any workspace member may join sessions
 */

import { useState, useMemo } from 'react';
import { Users, ChevronDown, ChevronUp, Check, Search, X, Copy } from 'lucide-react';
import { useSystemUsers } from '../../../hooks/useSystemUsers';
import type { ParticipantMode } from '../../../types/prioritization/tournament';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RoundParticipantConfig {
  roundId?: string;
  roundNumber: number;
  label: string;
  participantIds: string[];
}

export interface TournamentParticipantEditorProps {
  /** Current participant mode */
  mode: ParticipantMode;
  /** Default/fixed participant IDs (used when mode = 'fixed') */
  defaultParticipantIds: string[];
  /** Per-round participant configs (used when mode = 'per_round') */
  roundConfigs: RoundParticipantConfig[];

  /** Called when the mode changes */
  onModeChange: (mode: ParticipantMode) => void;
  /** Called when the shared (fixed) participant list changes */
  onDefaultParticipantsChange: (ids: string[]) => void;
  /** Called when a specific round's participant list changes */
  onRoundParticipantsChange: (roundNumber: number, ids: string[]) => void;

  /** Whether editing is allowed (false for COMPLETE rounds) */
  readonly?: boolean;
}

// ─── Mode option cards ────────────────────────────────────────────────────────

const MODE_OPTIONS: { value: ParticipantMode; label: string; desc: string }[] = [
  {
    value: 'fixed',
    label: 'Fixed',
    desc: 'Same participants in every round',
  },
  {
    value: 'per_round',
    label: 'Per round',
    desc: 'Different participants at each tier',
  },
  {
    value: 'open',
    label: 'Open',
    desc: 'Any workspace member can join',
  },
];

// ─── Member picker ────────────────────────────────────────────────────────────

function MemberPicker({
  selectedIds,
  onChange,
  readonly,
  placeholder = 'Search members…',
}: {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  readonly?: boolean;
  placeholder?: string;
}) {
  const { users, loading } = useSystemUsers();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const filtered = useMemo(
    () =>
      users.filter(
        (u) =>
          !selectedIds.includes(u.id) &&
          (u.name?.toLowerCase().includes(query.toLowerCase()) ||
            u.email?.toLowerCase().includes(query.toLowerCase()))
      ),
    [users, selectedIds, query]
  );

  const selectedUsers = useMemo(
    () => users.filter((u) => selectedIds.includes(u.id)),
    [users, selectedIds]
  );

  const addMember = (id: string) => {
    onChange([...selectedIds, id]);
    setQuery('');
  };

  const removeMember = (id: string) => {
    onChange(selectedIds.filter((x) => x !== id));
  };

  if (loading) {
    return (
      <p className="text-xs text-[var(--text-ter)] italic">Loading members…</p>
    );
  }

  return (
    <div className="space-y-2">
      {/* Selected chips */}
      {selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedUsers.map((u) => (
            <span
              key={u.id}
              className="inline-flex items-center gap-1 text-xs bg-primary-50 text-primary-700 border border-primary-200 rounded-full px-2.5 py-1"
            >
              <span className="w-5 h-5 rounded-full bg-primary-200 text-primary-700 flex items-center justify-center text-[10px] font-semibold shrink-0">
                {(u.name || u.email || '?')[0].toUpperCase()}
              </span>
              {u.name || u.email}
              {!readonly && (
                <button
                  type="button"
                  onClick={() => removeMember(u.id)}
                  className="ml-0.5 hover:text-primary-900 transition-colors"
                  aria-label={`Remove ${u.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {!readonly && (
        <div className="relative">
          <div
            className="flex items-center gap-2 border border-[var(--rule)] px-3 py-1.5 cursor-text bg-[var(--bg-raised)] hover:border-[var(--rule)] focus-within:border-[var(--slate)] focus-within:ring-2 focus-within:ring-indigo-100 transition-all"
            onClick={() => setOpen(true)}
          >
            <Search className="h-3.5 w-3.5 text-[var(--text-ter)] shrink-0" />
            <input
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
              placeholder={selectedIds.length === 0 ? placeholder : 'Add more…'}
              className="flex-1 text-sm bg-transparent outline-none placeholder-gray-400 text-[var(--text-pri)]"
            />
          </div>
          {open && filtered.length > 0 && (
            <div className="absolute z-50 left-0 right-0 mt-1 bg-[var(--bg-raised)] border border-[var(--rule)] shadow-lg overflow-hidden max-h-48 overflow-y-auto">
              {filtered.slice(0, 20).map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onMouseDown={() => addMember(u.id)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-[var(--surface)] text-left transition-colors"
                >
                  <span className="w-6 h-6 rounded-full bg-[var(--hover-tint)] text-[var(--text-sec)] flex items-center justify-center text-[10px] font-semibold shrink-0">
                    {(u.name || u.email || '?')[0].toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm text-[var(--text-pri)] truncate">{u.name}</p>
                    {u.name !== u.email && (
                      <p className="text-xs text-[var(--text-ter)] truncate">{u.email}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
          {open && query.length > 0 && filtered.length === 0 && (
            <div className="absolute z-50 left-0 right-0 mt-1 bg-[var(--bg-raised)] border border-[var(--rule)] shadow-lg px-3 py-2 text-sm text-[var(--text-ter)]">
              No matching members
            </div>
          )}
        </div>
      )}

      {selectedIds.length === 0 && readonly && (
        <p className="text-xs text-[var(--text-ter)] italic">No participants assigned</p>
      )}
    </div>
  );
}

// ─── Per-round row ────────────────────────────────────────────────────────────

function PerRoundRow({
  config,
  previousIds,
  onChange,
  readonly,
}: {
  config: RoundParticipantConfig;
  previousIds: string[] | null;
  onChange: (ids: string[]) => void;
  readonly?: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  const hasSameAsPrevious =
    previousIds !== null &&
    previousIds.length > 0 &&
    JSON.stringify([...config.participantIds].sort()) ===
      JSON.stringify([...previousIds].sort());

  const copyFromPrevious = () => {
    if (previousIds) onChange([...previousIds]);
  };

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-[var(--surface)] transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-semibold text-[var(--text-ter)] shrink-0">
            Round {config.roundNumber}
          </span>
          <span className="text-sm font-medium text-[var(--text-pri)] truncate">{config.label}</span>
          {config.participantIds.length > 0 && (
            <span className="text-xs text-[var(--text-ter)] shrink-0">
              {config.participantIds.length} participant{config.participantIds.length !== 1 ? 's' : ''}
            </span>
          )}
          {hasSameAsPrevious && (
            <span className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] shrink-0">same as previous</span>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="h-3.5 w-3.5 text-[var(--text-ter)] shrink-0" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)] shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-[var(--rule)] space-y-2">
          <MemberPicker
            selectedIds={config.participantIds}
            onChange={onChange}
            readonly={readonly}
          />
          {!readonly && previousIds && previousIds.length > 0 && (
            <button
              type="button"
              onClick={copyFromPrevious}
              className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors"
            >
              <Copy className="h-3 w-3" />
              Copy from Round {config.roundNumber - 1}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function TournamentParticipantEditor({
  mode,
  defaultParticipantIds,
  roundConfigs,
  onModeChange,
  onDefaultParticipantsChange,
  onRoundParticipantsChange,
  readonly = false,
}: TournamentParticipantEditorProps) {
  return (
    <div className="space-y-4">
      {/* Mode selector */}
      {!readonly && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {MODE_OPTIONS.map((opt) => {
            const active = mode === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onModeChange(opt.value)}
                className={` border px-3 py-2.5 text-left transition-all ${
                  active
                    ? 'border-[var(--slate)] bg-[var(--slate-dim)] ring-1 ring-indigo-300'
                    : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--rule)] hover:bg-[var(--surface)]'
                }`}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className={`text-sm font-medium ${active ? 'text-[var(--text-ter)]' : 'text-[var(--text-pri)]'}`}>
                    {opt.label}
                  </span>
                  {active && <Check className="h-3.5 w-3.5 text-[var(--text-ter)]" />}
                </div>
                <p className="text-xs text-[var(--text-ter)] leading-tight">{opt.desc}</p>
              </button>
            );
          })}
        </div>
      )}

      {readonly && (
        <div className="flex items-center gap-2 text-sm text-[var(--text-sec)]">
          <Users className="h-4 w-4 text-[var(--text-ter)]" />
          <span>
            {mode === 'fixed' && 'Fixed participants (same each round)'}
            {mode === 'per_round' && 'Per-round participants'}
            {mode === 'open' && 'Open — any workspace member'}
          </span>
        </div>
      )}

      {/* Fixed mode: single shared picker */}
      {mode === 'fixed' && (
        <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-3 space-y-2">
          <p className="text-xs font-medium text-[var(--text-sec)]">Participants (all rounds)</p>
          <MemberPicker
            selectedIds={defaultParticipantIds}
            onChange={onDefaultParticipantsChange}
            readonly={readonly}
          />
        </div>
      )}

      {/* Per-round mode: stacked rows */}
      {mode === 'per_round' && (
        <div className="space-y-2">
          {roundConfigs.map((rc, i) => (
            <PerRoundRow
              key={rc.roundNumber}
              config={rc}
              previousIds={i === 0 ? null : roundConfigs[i - 1].participantIds}
              onChange={(ids) => onRoundParticipantsChange(rc.roundNumber, ids)}
              readonly={readonly}
            />
          ))}
        </div>
      )}

      {/* Open mode: informational */}
      {mode === 'open' && (
        <div className="border border-[var(--slate)] bg-[var(--slate-dim)] px-4 py-3 flex items-start gap-3">
          <Users className="h-4 w-4 text-[var(--text-ter)] shrink-0 mt-0.5" />
          <p className="text-sm text-[var(--text-sec)]">
            Any workspace member with access may join sessions in this tournament.
            No explicit participant list is required.
          </p>
        </div>
      )}
    </div>
  );
}

export default TournamentParticipantEditor;
